import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { selectSuggestions } from '@/state/chatStore';
import { useChatActions, useChatConfig, useChatState } from './ChatProvider';
import { ChatHeader } from './parts/ChatHeader';
import { PastChatList, PastChatsScreen } from './parts/PastChats';
import { Transcript } from './parts/Transcript';
import { Suggestions } from './parts/Suggestions';
import { Composer } from './parts/Composer';
import { ErrorBanner } from './parts/ErrorBanner';
import { PrivateNotice } from './parts/PrivateNotice';
import { HomeScreen } from './parts/HomeScreen';
import { MessagesScreen } from './parts/MessagesScreen';
import { SpaceTabs } from './parts/SpaceTabs';
import { useFocusTrap } from '@/hooks/useFocusTrap';
/**
 * The conversation surface itself. Layout-agnostic: `mode` decides whether it
 * is a docked desktop panel, a full-screen mobile sheet, or an inline block.
 */
export function ChatPanel({ mode, onClose, state = 'open' }) {
    const { launcher, features, mode: chatMode } = useChatConfig();
    const { send, init, showView, loadPastChats } = useChatActions();
    const suggestions = useChatState(selectSuggestions);
    const isSending = useChatState((s) => s.isSending || s.isAgentTyping);
    const closed = useChatState((s) => s.inactivity === 'closed');
    const view = useChatState((s) => s.view);
    const panelRef = useRef(null);
    // Wider, or the whole page (features.expand). Docked panels only: a phone's
    // sheet already fills the screen. Leaving full screen returns to the size before it.
    const [size, setSize] = useState('normal');
    const [beforeFull, setBeforeFull] = useState('normal');
    const canExpand = features.expand && mode === 'panel';
    const shown = canExpand ? size : 'normal';
    const fullPage = shown === 'full';
    // Data mode's past chats: a screen of the panel, or a sidebar in full screen.
    const history = chatMode === 'data' && features.history;
    const sidebar = history && fullPage;
    const resize = (next) => {
        if (next === 'full')
            setBeforeFull(size);
        setSize(next === 'normal' && size === 'full' ? beforeFull : next);
    };
    // Sheets cover the page, so they behave as modal dialogs. Released while
    // exiting so focus doesn't fight the animation.
    useFocusTrap(panelRef, mode === 'sheet' && state === 'open', onClose);
    useEffect(() => {
        void init();
    }, [init]);
    // The sidebar shows the list as soon as full screen opens.
    useEffect(() => {
        if (sidebar)
            void loadPastChats();
    }, [sidebar, loadPastChats]);
    // Esc leaves the full-page view (the header button does too).
    useEffect(() => {
        if (!fullPage)
            return;
        const onKey = (event) => {
            if (event.key === 'Escape')
                setSize(beforeFull);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullPage, beforeFull]);
    // Prevent the page behind a mobile sheet, or the full-page view, from scrolling underneath it.
    useEffect(() => {
        if (mode !== 'sheet' && !fullPage)
            return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [mode, fullPage]);
    return (_jsx("div", { ref: panelRef, className: "ac-panel", "data-mode": mode, "data-chat": chatMode === 'data' ? 'data' : undefined, "data-expanded": shown === 'normal' ? undefined : shown, "data-state": state, "data-view": view, "data-position": launcher.position, role: mode === 'sheet' ? 'dialog' : 'region', "aria-modal": mode === 'sheet' || undefined, "aria-label": chatMode === 'data' ? 'Private business data chat' : 'Chat', children: view !== 'chat' ? (_jsxs(_Fragment, { children: [view === 'history' ? (_jsx(PastChatsScreen, { onClose: onClose, closeIcon: mode === 'panel' ? 'minimize' : 'close' })) : view === 'home' ? (_jsx(HomeScreen, { onClose: onClose, closeIcon: mode === 'panel' ? 'minimize' : 'close' })) : (_jsx(MessagesScreen, { onClose: onClose, closeIcon: mode === 'panel' ? 'minimize' : 'close' })), view !== 'history' && _jsx(SpaceTabs, {})] })) : (_jsxs(_Fragment, { children: [_jsx(ChatHeader, { onClose: onClose, closeIcon: mode === 'panel' ? 'minimize' : 'close', size: shown, onSize: canExpand ? resize : undefined, onHistory: history && !sidebar ? () => showView('history') : undefined }), _jsxs("div", { className: "ac-split", children: [sidebar && (_jsx("aside", { className: "ac-side", children: _jsx(PastChatList, {}) })), _jsxs("div", { className: "ac-main", children: [chatMode === 'data' && _jsx(PrivateNotice, {}), _jsx(Transcript, {}), _jsx(ErrorBanner, {}), features.suggestions && (_jsx(Suggestions, { suggestions: suggestions, disabled: isSending || closed, onPick: (text) => void send(text) })), _jsx(Composer, { autoFocus: mode !== 'sheet' })] })] })] })) }));
}
